import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '@/features/settings/api';
import { PAGE_COPY_BASE } from '@/features/pageCopy/routes';
import { keys } from '@/lib/query/keys';
import { useLocale } from '@/lib/i18n/useLocale';
import { formatRelative } from '@/lib/format';
import { DataView } from '@/lib/ui/states';
import { ContentStatusPill } from '@/lib/ui/StatusPill';
import { cn } from '@/lib/ui/cn';
import type { PageOutline } from '@/lib/api/types';
import { IconChevron, IconClose, IconExternal, IconSearch } from './icons';
import { config } from '@/lib/config';
import { useAuth } from '@/lib/auth/useAuth';
import { pagesApi } from '@/features/pages/api';
import { morphToEditor } from '@/features/website/morph';
import { AttentionStrip } from '@/features/website/AttentionStrip';
import { editorPath, resolveLabel } from './outline';
import { useOutlines } from './useOutlines';

const INTRO_KEY = 'tepco.siteMap.intro.v2';

/**
 * The website home, where every session lands: every page of the website in navigation
 * order, grouped the way the footer groups them, then the special screens (404,
 * maintenance…). Opening one goes straight to its editor, the page's sections in on-page
 * order beside the live page, with the tile growing into that page. Above them, what is
 * waiting on someone, and on a first visit, how editing works.
 */
export function SiteMapPage() {
  // Settings is an administrator's screen: only they get the link to the logos.
  const canBrand = useAuth().can('settings:manage');
  const { t } = useTranslation();
  const { locale } = useLocale();
  const outlines = useOutlines();
  // Arabic completeness per namespace, from the page-copy index (one cheap call).
  const copyIndex = useQuery({
    queryKey: keys.settings.pageCopy(),
    queryFn: () => settingsApi.listPageCopy(),
  });
  const arDone = useMemo(
    () => new Map((copyIndex.data ?? []).map((n) => [n.namespace, n.ar_complete])),
    [copyIndex.data],
  );

  const [filter, setFilter] = useState('');
  const [intro, setIntro] = useState(() => {
    try {
      return localStorage.getItem(INTRO_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const closeIntro = () => {
    setIntro(false);
    try {
      localStorage.setItem(INTRO_KEY, '1');
    } catch {
      /* private mode */
    }
  };

  return (
    <div>
      <header className="mb-lg flex flex-wrap items-end justify-between gap-md border-b-[0.5px] border-hairline pb-md">
        <div className="flex max-w-[68ch] flex-col gap-2xs">
          <h1 className="text-headline">{t('website.home.title')}</h1>
          <p className="text-body text-ink-mid">
            {outlines.data
              ? t('website.home.lead', {
                  pages: outlines.data.outlines.filter((o) => o.page).length,
                  screens: outlines.data.outlines.filter((o) => !o.page).length,
                })
              : t('website.home.leadShort')}
          </p>
        </div>
        <a
          href={`${config.siteBaseUrl}/${locale}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-[40px] items-center gap-2xs rounded-sm px-xs text-small text-accent-blue transition-colors duration-almanac hover:bg-canvas-paper hover:text-ink-deep focus-visible:shadow-focus"
        >
          {t('website.home.openSite')}
          <IconExternal />
        </a>
      </header>

      <AttentionStrip />

      {intro && (
        <section
          aria-labelledby="site-map-intro"
          className="mb-lg grid gap-md rounded-md border-[0.5px] border-hairline bg-canvas-paper p-lg md:grid-cols-[1fr_auto]"
        >
          <div className="flex flex-col gap-sm">
            <h2 id="site-map-intro" className="text-component-title font-medium text-ink-deep">
              {t('website.home.welcome.title')}
            </h2>
            <ol className="grid gap-md text-small text-ink-mid md:grid-cols-3">
              {(['one', 'two', 'three'] as const).map((k, i) => (
                <li key={k} className="flex gap-sm">
                  <span
                    aria-hidden
                    className="tnum inline-flex size-[24px] shrink-0 items-center justify-center rounded-pill bg-canvas-sand font-mono text-label text-accent-blue"
                  >
                    {i + 1}
                  </span>
                  <span className="pt-[2px]">{t(`website.home.welcome.${k}`)}</span>
                </li>
              ))}
            </ol>
            <p className="text-small text-ink-mute">{t('website.home.welcome.note')}</p>
            {canBrand ? (
              <p className="max-w-[80ch] text-small text-ink-mid">
                {t('website.home.welcome.pictures')}{' '}
                <Link
                  to="/settings#brand-assets"
                  className="text-accent-blue underline-offset-4 hover:text-ink-deep hover:underline focus-visible:shadow-focus"
                >
                  {t('website.home.welcome.brandLink')}
                </Link>
              </p>
            ) : (
              <p className="max-w-[80ch] text-small text-ink-mid">
                {t('website.home.welcome.picturesNoSettings')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={closeIntro}
            className="inline-flex items-center gap-2xs self-start rounded-sm px-xs py-2xs text-small text-ink-mute hover:text-ink-deep focus-visible:shadow-focus"
          >
            <IconClose />
            {t('website.home.welcome.dismiss')}
          </button>
        </section>
      )}

      <label className="relative mb-lg block max-w-[420px]">
        <span className="sr-only">{t('siteMap.find')}</span>
        <IconSearch className="pointer-events-none absolute start-sm top-1/2 -translate-y-1/2 text-ink-mute" />
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('siteMap.find')}
          className="min-h-[44px] w-full rounded-sm border-[0.5px] border-hairline-strong bg-canvas-paper py-xs pe-sm ps-[36px] text-small placeholder:text-ink-mute focus-visible:shadow-focus focus-visible:outline-none"
        />
      </label>

      <DataView
        query={outlines}
        copy={{ loading: t('states.pages.loading'), errorTitle: t('states.pages.errorTitle') }}
      >
        {(sm) => {
          const q = filter.trim().toLowerCase();
          const visible = (o: PageOutline) =>
            !q ||
            resolveLabel(o.label, 'en').toLowerCase().includes(q) ||
            resolveLabel(o.label, 'ar').includes(filter.trim()) ||
            o.path.toLowerCase().includes(q) ||
            o.regions.some((r) => resolveLabel(r.label, locale).toLowerCase().includes(q));
          const groups = sm.groups
            .map((g) => ({ g, items: sm.outlines.filter((o) => o.group === g.key && visible(o)) }))
            .filter((x) => x.items.length > 0);
          if (groups.length === 0) {
            return (
              <p className="text-small text-ink-mid">
                {t('siteMap.noMatch', { q: filter.trim() })}
              </p>
            );
          }
          return (
            <div className="flex flex-col gap-xl">
              {groups.map(({ g, items }) => (
                <section key={g.key} aria-labelledby={`grp-${g.key}`}>
                  <h2
                    id={`grp-${g.key}`}
                    className="mb-sm font-mono text-label uppercase tracking-[0.1em] text-ink-mute"
                  >
                    {resolveLabel(g.label, locale)}
                  </h2>
                  {g.key === 'screens' && (
                    <p className="mb-sm max-w-prose text-small text-ink-mid">
                      {t('siteMap.screensLead')}
                    </p>
                  )}
                  <ul className="grid gap-sm md:grid-cols-2 2xl:grid-cols-3">
                    {items.map((o) => (
                      <li key={o.key}>
                        <PageTile outline={o} locale={locale} arDone={arDone} />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
              <p className="text-small text-ink-mute">
                {t('siteMap.advanced')}{' '}
                <Link
                  to={PAGE_COPY_BASE}
                  className="text-accent-blue underline-offset-4 hover:underline"
                >
                  {t('siteMap.advancedLink')}
                </Link>
              </p>
            </div>
          );
        }}
      </DataView>
    </div>
  );
}

function PageTile({
  outline,
  locale,
  arDone,
}: {
  outline: PageOutline;
  locale: 'en' | 'ar';
  arDone: Map<string, boolean>;
}) {
  const { t } = useTranslation();
  const sections = outline.regions.filter((r) => r.on_page).length;
  const own = outline.namespaces.filter(
    (ns) => !outline.regions.every((r) => r.copy.every((c) => c.namespace !== ns || c.bundled)),
  );
  const missingAr = own.some((ns) => arDone.get(ns) === false);
  const other = locale === 'ar' ? 'en' : 'ar';
  const to = editorPath(outline);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const title = useRef<HTMLSpanElement>(null);
  // Warm the editor while the pointer is on its way, so it is ready for the tile to grow into.
  const warm = () => {
    void import('@/features/pages/PageEditorPage');
    void import('./ScreenEditorPage');
    const id = outline.record?.id;
    if (id)
      void qc.prefetchQuery({
        queryKey: keys.pages.detail(id),
        queryFn: () => pagesApi.get(id),
        staleTime: 30_000,
      });
  };
  return (
    <Link
      to={to}
      onPointerEnter={warm}
      onFocus={warm}
      onClick={(e) => {
        if (
          e.defaultPrevented ||
          e.button !== 0 ||
          e.metaKey ||
          e.ctrlKey ||
          e.shiftKey ||
          e.altKey
        )
          return;
        if (morphToEditor(e.currentTarget, title.current, () => navigate(to))) e.preventDefault();
      }}
      className={cn(
        'group flex h-full flex-col gap-xs rounded-md border-[0.5px] border-hairline bg-canvas-paper px-md py-sm',
        'transition-[transform,border-color] duration-almanac ease-almanac hover:-translate-y-[2px] hover:border-accent-blue focus-visible:shadow-focus motion-reduce:hover:translate-y-0',
      )}
    >
      <span className="flex items-start justify-between gap-sm">
        <span className="flex min-w-0 flex-col">
          <span ref={title} className="self-start text-component-title font-medium text-ink-deep">
            {resolveLabel(outline.label, locale)}
          </span>
          <span
            className="self-start text-small text-ink-mute"
            dir={other === 'ar' ? 'rtl' : 'ltr'}
            lang={other}
          >
            {resolveLabel(outline.label, other)}
          </span>
        </span>
        <IconChevron className="mt-[4px] shrink-0 text-ink-mute transition-transform duration-almanac group-hover:translate-x-[2px] rtl:-scale-x-100 rtl:group-hover:-translate-x-[2px]" />
      </span>
      <span className="mt-auto flex flex-wrap items-center gap-x-sm gap-y-2xs pt-2xs text-small text-ink-mid">
        {outline.path && (
          <span className="font-mono text-label text-ink-mute" dir="ltr">
            {outline.path}
          </span>
        )}
        <span className="tnum">{t('siteMap.sections', { count: sections })}</span>
        {missingAr && (
          <span className="text-label-sm uppercase tracking-[0.1em] text-signal-amber-ink">
            {t('siteMap.arabicMissing')}
          </span>
        )}
        {outline.record && outline.record.status !== 'published' && (
          <ContentStatusPill status={outline.record.status} />
        )}
        {outline.record && (
          <span className="ms-auto text-label-sm text-ink-mute">
            {formatRelative(outline.record.updated_at)}
          </span>
        )}
      </span>
    </Link>
  );
}
